/**
 * API Interceptor with Okta Authentication
 * Handles token injection, error handling, and tenant context
 */
import axios from 'axios';
import { getApiBaseUrl } from './api';
import oktaAuth from '../config/okta';
import { AI_USAGE_CHANGED_EVENT } from '../constants/aiUsage';

let oktaAuthInstance = null;

/** Requests that spend AI tokens, and so change the tenant's usage meter. */
const AI_SPENDING_ENDPOINTS = ['/widgets/execute'];

/**
 * Announce that AI tokens were just spent.
 *
 * Broadcasting from the one place every AI request already passes through keeps
 * the usage meter live without each widget component having to know it exists.
 */
const notifyAiUsageChanged = (response) => {
  try {
    const url = response?.config?.url || '';
    const method = (response?.config?.method || '').toLowerCase();
    if (method !== 'post' || !AI_SPENDING_ENDPOINTS.some((endpoint) => url.includes(endpoint))) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent(AI_USAGE_CHANGED_EVENT, {
        // The execute response carries the refreshed summary, so a listener can
        // use it directly instead of re-fetching.
        detail: { usage: response?.data?.data?.aiUsage ?? null },
      }),
    );
  } catch (error) {
    // Never let telemetry break the response the caller is waiting for
    console.warn('Could not broadcast AI usage change:', error);
  }
};

export const initializeApiInterceptor = (oktaAuth) => {
  oktaAuthInstance = oktaAuth;
};

export const createApiClient = (authState, tenantId = null) => {
  const instance = axios.create({
    baseURL: getApiBaseUrl(),
    timeout: 300000, //5 min for large file upload
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - inject auth token and tenant context
  instance.interceptors.request.use(
    async (config) => {
      try {
        if (authState?.isAuthenticated) {
          const idTokenObj = await oktaAuth.tokenManager.get('idToken');
          const idToken =
            (typeof idTokenObj?.idToken === 'string' && idTokenObj.idToken) ||
            (typeof idTokenObj?.value === 'string' && idTokenObj.value) ||
            authState?.idToken?.idToken ||
            null;
          if (!idToken) {
            console.error('Authenticated session missing ID token; cannot call API');
            return Promise.reject(new Error('Missing ID token'));
          }
          config.headers['X-Id-Token'] = idToken;

          const accessToken = await oktaAuth.getAccessToken();
          if (accessToken) {
            config.headers.Authorization = `Bearer ${accessToken}`;
          }
        }

        // Inject tenant context if provided
        if (tenantId) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        // Add subscription key if configured
        if (process.env.REACT_APP_SUBS_KEY) {
          config.headers['Subscription-Key'] = process.env.REACT_APP_SUBS_KEY;
        }

        // Handle FormData - remove Content-Type so axios can set it with boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      } catch (error) {
        console.error('Error in request interceptor:', error);
        return Promise.reject(error);
      }
    },
    (error) => {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors and format responses
  instance.interceptors.response.use(
    (response) => {
      notifyAiUsageChanged(response);
      return response;
    },
    (error) => {
      // Handle specific error cases
      if (error.response?.status === 401) {
        // Unauthorized - redirect to login
        console.error('Unauthorized access - redirecting to login');
        window.location.href = '/signin';
      } else if (error.response?.status === 403) {
        // Forbidden - user doesn't have permission
        console.error('Access forbidden - insufficient permissions');
      } else if (error.response?.status === 404) {
        // Not found
        console.error('Resource not found');
      } else if (error.response?.status === 400) {
        // Bad request
        console.error('Bad request:', error.response?.data);
      } else if (error.response?.status >= 500) {
        // Server error
        console.error('Server error - please try again later');
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

/**
 * React hook to create and use API client with auth
 * Usage:
 *   const api = useApiWithAuth();
 *   const response = await api.get('/endpoint');
 */
export const useApiWithAuth = () => {
  // This will be imported and used in components
  // The actual implementation is in hooks/useApi.js
  throw new Error('useApiWithAuth should be imported from hooks/useApi.js');
};

export default createApiClient;





